import React, { ReactNode, useRef, forwardRef, useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import Link from 'next/link';
import { v4 } from 'uuid';
import _ from 'lodash';

import useUI from 'src/hooks/use-ui';
import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';
import { VisuallyHidden } from 'shared/components/visually-hidden';
import { EmbeddedExternalLink } from 'src/components/core';
import ChevronIcon from 'src/assets/chevron-icon';

type CarouselV2Props = {
  children: ReactNode;
  href?: string;
  hideTitle?: boolean;
  title?: string;
  subTitle?: string;
  icon?: ReactNode;
  hideLink?: boolean;
  header?: ReactNode;
  mt?: string;
  gradientColor?: string;
  isSmallTitle?: boolean;
  isBottomSubTitle?: boolean;
  onLinkClick?: () => void;
  /**
   * Enable lazy rendering of carousel items. Items will only render when they come into view.
   * @default true
   */
  enableLazyRendering?: boolean;
  /**
   * Root margin for intersection observer when lazy rendering is enabled.
   * Positive values will render items before they come into view.
   * @default '200px'
   */
  lazyRootMargin?: string;
  /**
   * Placeholder to show while lazy rendering is enabled.
   * @default <LazyPlaceholder />
   */
  lazyPlaceholder?: ReactNode;
};

/**
 * Custom hook for lazy carousel item intersection observation
 */
const useLazyCarouselItem = (
  root: Element | null,
  rootMargin: string
): { hasRendered: boolean; itemRef: React.RefObject<HTMLDivElement> } => {
  const [hasRendered, setHasRendered] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!root || !itemRef.current || hasRendered) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasRendered(true);
        }
      },
      {
        root,
        rootMargin,
        threshold: 0,
      }
    );

    observer.observe(itemRef.current);

    return (): void => {
      observer.disconnect();
    };
  }, [root, rootMargin, hasRendered]);

  return { hasRendered, itemRef };
};

/**
 * LazyCarouselItem handles the lazy rendering logic for individual carousel items
 */
const LazyCarouselItem = ({
  children,
  rootMargin = '200px',
  placeholder,
  root,
}: {
  children: ReactNode;
  rootMargin?: string;
  placeholder: ReactNode;
  root?: Element | null;
}): JSX.Element => {
  const { hasRendered, itemRef } = useLazyCarouselItem(root ?? null, rootMargin);

  if (hasRendered) {
    return <>{children}</>;
  }

  return <div ref={itemRef}>{placeholder}</div>;
};

/**
 * Default placeholder for lazy carousel items
 */
const LazyPlaceholder = (): JSX.Element => (
  <PlaceholderWrapper>
    <PlaceholderContent />
  </PlaceholderWrapper>
);

export const CarouselV2 = forwardRef<HTMLDivElement, CarouselV2Props>(
  (props, forwardedRef): JSX.Element => {
    const {
      children,
      href,
      hideTitle = false,
      title,
      subTitle,
      icon,
      hideLink = false,
      header,
      mt = '32px',
      gradientColor = '#ffffff',
      isSmallTitle = false,
      isBottomSubTitle = false,
      onLinkClick,
      enableLazyRendering = false,
      lazyRootMargin = '200px',
      lazyPlaceholder = <LazyPlaceholder />,
    } = props;

    const { isEmbeddedCarousel } = useUI();

    const viewportRef = useRef<HTMLDivElement | null>(null);
    const itemsRef = useRef<HTMLDivElement>(null);
    const startWatcherRef = useRef(null);
    const endWatcherRef = useRef<HTMLDivElement>(null);
    const idRef = useRef(v4());
    const id = idRef.current;

    // Track viewport element in state so intersection observers can react to it being mounted
    const [viewportElement, setViewportElement] = useState<HTMLDivElement | null>(null);

    // Create a stable merged ref callback that updates both internal and forwarded refs
    // We intentionally omit forwardedRef from deps to prevent recreating the callback
    const mergedViewportRef = useCallback((node: HTMLDivElement | null) => {
      // Update internal ref
      viewportRef.current = node;

      // Update state so intersection observers can react
      setViewportElement(node);

      // Update forwarded ref
      if (forwardedRef) {
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else {
          forwardedRef.current = node;
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startWatcher = useIntersectionObserver(startWatcherRef, { root: viewportElement });
    const endWatcher = useIntersectionObserver(endWatcherRef, { root: viewportElement });

    // 16px to account for shift of container to account for shadow padding
    const buffer = 16;
    const viewportLeft = viewportRef.current?.getBoundingClientRect().left ?? 0;

    const handlePrevNextClick = (direction: -1 | 1): void => {
      if (!viewportRef.current || !itemsRef.current?.children.length) {
        return;
      }

      const { clientWidth: containerWidth } = viewportRef.current;
      const elements = Array.from(itemsRef.current.children);
      let target: Element | undefined;
      let scrollBy: number;

      // Next
      if (direction === 1) {
        target = elements.find((el) => {
          const { left, width } = el.getBoundingClientRect();
          return left + width - viewportLeft > containerWidth;
        });

        if (!target) {
          return;
        }

        scrollBy = target.getBoundingClientRect().left - viewportLeft - buffer;
        viewportRef.current.scrollBy({ left: scrollBy, behavior: 'smooth' });

        // Previous
      } else {
        target = elements.reverse().find((el) => {
          const { left } = el.getBoundingClientRect();
          return left - viewportLeft < 0;
        });

        if (!target) {
          viewportRef.current.scrollBy({ left: 0, behavior: 'smooth' });
          return;
        }

        scrollBy = (containerWidth - (target.getBoundingClientRect().right - viewportLeft) - buffer) * -1;
      }

      viewportRef.current.scrollBy({ left: scrollBy, behavior: 'smooth' });
    };

    // Wrap children with lazy rendering if enabled
    const renderChildren = (): ReactNode => {
      if (!enableLazyRendering) {
        return children;
      }

      return React.Children.map(children, (child, index) => {
        // Create a stable key using child props or fallback to index
        const childKey = React.isValidElement(child) && child.key ? child.key : `carousel-item-${index}`;

        return (
          <LazyCarouselItem
            key={childKey}
            rootMargin={lazyRootMargin}
            placeholder={lazyPlaceholder}
            root={viewportElement}
          >
            {child}
          </LazyCarouselItem>
        );
      });
    };

    return (
      <Wrapper $mt={mt}>
        <WrapperShift>
          {header ||
            ((!hideTitle || (!hideLink && href)) && (
              <Header>
                {!hideTitle && title && (
                  <TextWrapper>
                    {icon}

                    <div>
                      <Heading
                        id={id}
                        $isSmallTitle={isSmallTitle}
                        data-testid={`${_.kebabCase(title)}-carousel-heading`}
                      >
                        {title}
                      </Heading>
                      {subTitle && !isBottomSubTitle && <SubTitle>{subTitle}</SubTitle>}
                    </div>
                  </TextWrapper>
                )}

                {!hideLink &&
                  href &&
                  (isEmbeddedCarousel ? (
                    <ViewAllEmbedded href={href}>View All</ViewAllEmbedded>
                  ) : (
                    <Link href={href}>
                      <ViewAll href={href} onClick={onLinkClick}>
                        View All
                      </ViewAll>
                    </Link>
                  ))}
              </Header>
            ))}

          <SliderContainer role='group' aria-roledescription='carousel' aria-labelledby={id}>
            <Viewport ref={mergedViewportRef}>
              <ItemsContainer>
                <GradientLeft isVisible={!startWatcher?.isIntersecting} $gradientColor={gradientColor} />

                <div ref={startWatcherRef} />

                <Items ref={itemsRef} $isBottomSubTitle={isBottomSubTitle}>
                  {renderChildren()}
                </Items>

                <EndWatcher ref={endWatcherRef} />

                <GradientRight isVisible={!endWatcher?.isIntersecting} $gradientColor={gradientColor} />
              </ItemsContainer>
            </Viewport>

            <PrevButton isVisible={!startWatcher?.isIntersecting} onClick={() => handlePrevNextClick(-1)}>
              <VisuallyHidden>Previous</VisuallyHidden>
              <PrevArrow />
            </PrevButton>

            <NextButton isVisible={!endWatcher?.isIntersecting} onClick={() => handlePrevNextClick(1)}>
              <VisuallyHidden>Next</VisuallyHidden>
              <NextArrow />
            </NextButton>
          </SliderContainer>
          {subTitle && isBottomSubTitle && <SubTitle isBottomSubTitle>{subTitle}</SubTitle>}
        </WrapperShift>
      </Wrapper>
    );
  }
);

const Wrapper = styled.div<{ $mt?: string }>`
  margin: 0 auto;
  max-width: var(--dct--customized-maxSiteWidth);

  ${({ $mt }) => css`
    margin-top: ${$mt};
  `};
`;

const WrapperShift = styled.div`
  margin: 0 -16px;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 0 16px;
  width: 100%;
`;

const TextWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const Heading = styled.h2<{ $isSmallTitle?: boolean }>`
  font-weight: bold;
  line-height: ${24 / 20};

  ${({ $isSmallTitle }) =>
    $isSmallTitle
      ? css`
          font-size: 16px;
        `
      : css`
          font-size: 20px;

          @media screen and (min-width: 600px) {
            font-size: 26px;
          }
        `};
`;

const SubTitle = styled.p<{ isBottomSubTitle?: boolean }>`
  color: #828a8f;
  font-size: 12px;
  line-height: ${16 / 12};
  margin-top: 4px;

  ${({ isBottomSubTitle }) =>
    isBottomSubTitle &&
    css`
      color: #646d72;
      margin-top: 0;
      padding: 24px 20px;
    `};
`;

const viewAllLinkStyles = css`
  color: var(--dct--customized-colors-buttonsLinks);
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: ${20 / 14};
  margin-left: auto;
  white-space: nowrap;
`;

const ViewAll = styled.a`
  ${viewAllLinkStyles};
`;

const ViewAllEmbedded = styled(EmbeddedExternalLink)`
  ${viewAllLinkStyles};
`;

const SliderContainer = styled.div`
  position: relative;
  isolation: isolate;
`;

const Viewport = styled.div`
  overflow-x: auto;
  -ms-overflow-style: none;
  scroll-behavior: smooth;
  scrollbar-width: none;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Gradient = styled.div<{ isVisible?: boolean }>`
  display: block;
  height: 100%;
  /* Use transform to force GPU acceleration */
  transform: translateZ(0);
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: none;
  position: absolute;
  top: 0;
  will-change: opacity;
  transition: opacity 0.1s;
  width: 40px;
  z-index: 10;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: 100px;
  }
`;

const GradientLeft = styled(Gradient)<{ $gradientColor?: string }>`
  background: linear-gradient(
    to left,
    ${({ $gradientColor }) => `${String($gradientColor)}00`},
    ${({ $gradientColor }) => $gradientColor} 100%
  );
  left: 0;
`;

const GradientRight = styled(Gradient)<{ $gradientColor?: string }>`
  background: linear-gradient(
    to right,
    ${({ $gradientColor }) => `${String($gradientColor)}00`},
    ${({ $gradientColor }) => $gradientColor} 100%
  );
  right: 0;
`;

const EndWatcher = styled.div`
  flex-shrink: 0;
  width: 1px;
`;

// Need the watcher divs to have a height
const ItemsContainer = styled.div`
  display: flex;
`;

export const Items = styled.div<{ $isBottomSubTitle?: boolean }>`
  display: flex;
  flex-basis: 100%;
  gap: 12px;
  padding: 20px 16px;

  ${({ $isBottomSubTitle }) =>
    $isBottomSubTitle &&
    css`
      padding-bottom: 0;
    `};
`;

const PlaceholderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  min-width: 220px;
  background-color: ${({ theme }) => theme.colors.grey[95]};
  border-radius: 8px;
  flex-shrink: 0;
  padding: 20px;
`;

const PlaceholderContent = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.grey[85]};
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

const prevNextButtonStyles = css`
  appearance: none;
  align-items: center;
  background: ${({ theme }) => theme.colors.grey[95]};
  border: none;
  border-radius: 50%;
  box-shadow: 0 3px 5px 0 #0000002b;
  cursor: pointer;
  display: none;
  height: 48px;
  justify-content: center;
  padding: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  will-change: opacity, visibility;
  transition: opacity 0.25s;
  opacity: 1;
  width: 48px;
  z-index: 11;

  &:hover {
    background-color: ${({ theme }) => theme.colors.blueGrey[90]};
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    display: flex;
  }

  ${({ isVisible }) =>
    !isVisible &&
    css`
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    `};
`;

const PrevButton = styled.button`
  ${prevNextButtonStyles}
  left: 8px;
`;

const NextButton = styled.button`
  ${prevNextButtonStyles}
  right: 8px;
`;

const prevNextArrowStyles = css`
  height: 10px;
  width: 17px;
`;

const PrevArrow = styled(ChevronIcon)`
  ${prevNextArrowStyles}
  transform: rotate(90deg);
`;
const NextArrow = styled(ChevronIcon)`
  ${prevNextArrowStyles}
  transform: rotate(-90deg);
`;
